/**
 * ===================================================================================
 * 📁 경로: services/frontend/src/pages/ReportsListPage.js
 * 📌 프로그램명: 분석 리포트 목록 페이지
 * 🧩 개요:
 * - 사용자가 생성한 모든 AI 건강 분석 리포트의 이력을 목록 형태로 보여주는 페이지입니다.
 * - 검색, 필터링, 페이지네이션 기능을 제공하여 사용자가 원하는 리포트를 쉽게 찾을 수 있도록 돕습니다.
 * - 각 리포트별 실천 계획의 진행 상황을 요약하여 표시하고, 원본 PDF 다운로드 및
 * 상세 페이지로의 이동 기능을 제공합니다.
 * ===================================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import MessageModal from '../components/MessageModal';
import { authHeader, getUserNoFromToken } from '../utils/auth';

/**
 * [Why]
 * 사용자가 자신의 모든 AI 분석 리포트 이력을 한 곳에서 관리하고 조회할 수 있는 UI를
 * 제공합니다. 이는 사용자가 자신의 건강 상태 변화를 시간의 흐름에 따라 추적하고,
 * 과거의 분석 내용을 다시 확인하며 주체적인 건강 관리를 할 수 있도록 지원하는
 * 핵심적인 기능 페이지입니다.
 *
 * [How]
 * - **상태 관리**: `useState` 훅을 사용하여 리포트 목록(items), 로딩/에러 상태, 페이지네이션(page),
 * 검색어(query) 등 페이지의 모든 동적인 상태를 관리합니다.
 * - **데이터 조회**: `userNo`와 현재 페이지(`page`), 검색 조건이 변경될 때마다 `useEffect` 훅을
 * 트리거하여 `load` 함수를 호출합니다. `load` 함수는 백엔드 API에 GET 요청을 보내
 * 필터링 및 페이지네이션이 적용된 리포트 목록을 비동기적으로 가져옵니다.
 * - **성능 최적화**:
 * - N+1 문제 방지: 백엔드 API에 `with_plan_counts=true` 파라미터를 보내, 리포트 목록 조회 시
 * 각 리포트의 실천 계획 통계까지 한 번에 가져오도록 요청합니다. 서버가 이를 지원하지 않을 경우에만
 * 개별적으로 상세 정보를 조회하는 폴백(Fallback) 로직을 수행합니다.
 * - 페이지네이션 최적화: 서버가 전체 아이템 수를 반환하지 않는 경우를 대비하여, 다음 페이지에
 * 데이터가 있는지 미리 조회(`probe` 함수)하여 전체 페이지 수를 추론하는 로직을 포함합니다.
 * - **원본 PDF 열기**: 사용자가 'PDF 보기' 버튼을 클릭하면 `openPdf` 함수가 실행되어,
 * 백엔드로부터 PDF 파일을 Blob 형태로 받아 새 탭에서 안전하게 엽니다.
 *
 * @component
 * @returns {JSX.Element} ReportsListPage 컴포넌트의 JSX 엘리먼트.
 */
function ReportsListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isApp = location?.pathname?.startsWith('/app');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0); // 페이지네이션: 0-based
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 목록 조회 및 PDF 열기 오류를 모달창으로 안내하기 위한 상태
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [planStats, setPlanStats] = useState({}); // { [report_id]: { total, completed, in_progress, not_started } }
  const [userNo, setUserNo] = useState('');
  const [query, setQuery] = useState('');
  const [includeSuperseded, setIncludeSuperseded] = useState(true);
  const [totalPages, setTotalPages] = useState(null); // 전체 페이지 수(알 수 없으면 null)
  const [downloadingId, setDownloadingId] = useState(null);

  // 컴포넌트 마운트 시 토큰 또는 로컬 스토리지에서 사용자 번호를 가져와 상태에 설정합니다.
  useEffect(() => {
    const tokenSub = getUserNoFromToken();
    if (tokenSub) setUserNo(tokenSub);
    else {
      const stored = window.localStorage.getItem('user_no');
      setUserNo(stored ? String(stored) : '');
    }
  }, []);

  // totalPages가 확정되었는데 현재 page가 초과 상태면 보정 (예: 라벨이 3/2로 보이는 문제 방지)
  useEffect(() => {
    if (totalPages !== null && totalPages >= 1 && page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, page]);

  // API URL과 날짜 포맷터를 메모이제이션하여 불필요한 재생성을 방지합니다.
  const apiUrl = useMemo(() => process.env.REACT_APP_API_URL || 'http://localhost:8000', []);

  const getFriendlyAxiosMessage = useCallback((e, fallback) => {
    // [변경 사유]
    // - 빌드 직후/재기동 직후에는 백엔드가 아직 준비되지 않아 axios에서 'Network Error'가 발생할 수 있습니다.
    // - 이 문구를 그대로 사용자에게 노출하면 불필요하게 심각해 보이므로, 준비중 안내로 치환합니다.
    if (typeof axios.isAxiosError === 'function' && axios.isAxiosError(e)) {
      if (e && e.message === 'Network Error' && !e.response) {
        // [변경 사유]
        // - 서비스 기동 직후에는 API가 아직 준비되지 않아 네트워크 오류가 발생할 수 있습니다.
        // - 사용자가 원인을 이해할 수 있도록 "기동 준비중" 안내 문구로 치환합니다.
        return '현재 서비스 준비중입니다. 잠시 후 다시 시도해 주세요.';
      }
      return (
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        fallback
      );
    }
    if (e && typeof e === 'object' && 'message' in e) {
      return e.message || fallback;
    }
    return fallback;
  }, []);

  /**
   * [Why]
   * 서버로부터 사용자 리포트 목록을 비동기적으로 조회하고, 페이지의 상태를 업데이트합니다.
   * 검색, 필터링, 페이지네이션 등 모든 데이터 조회 로직이 이 함수에 집중되어 있습니다.
   *
   * [How]
   * - 현재 상태(userNo, query, page 등)를 기반으로 API 요청 파라미터를 구성합니다.
   * - `axios`를 사용하여 백엔드 `/api/v1/users/{userNo}/reports` 엔드포인트를 호출합니다.
   * - 성공 시 응답 데이터를 `items` 상태에 저장하고, 응답 헤더나 본문에서 전체 아이템 수를
   * 추론하여 `totalPages` 상태를 업데이트합니다.
   * - 실패 시 에러 메시지를 `error` 상태에 저장합니다.
   */
  const load = useCallback(async () => {
    // userNo가 없으면 불필요한 호출을 막고 로딩 종료
    if (!userNo) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (!includeSuperseded) params.set('include_superseded', 'false');
      // 요약 전용 요청: 서버가 미지원해도 무해(무시됨). plan_counts 제공 시 N+1 제거
      // title(환자명#report_id)을 화면에 표시하기 위해 title 필드를 함께 요청합니다.
      params.set('fields', 'report_id,created_at,is_latest,summary,title');
      params.set('with_plan_counts', 'true');
      // 페이지당 3개 제한 및 오프셋
      params.set('limit', '3');
      params.set('offset', String(page * 3));
      const resp = await axios.get(`${apiUrl}/api/v1/users/${userNo}/reports?${params.toString()}`, { headers: authHeader() });
      const data = resp?.data;
      const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      setItems(list);
      // 총 개수 탐색: 헤더 우선, 그 다음 바디 내 total/pagination.total
      const headerTotal = Number(resp?.headers?.['x-total-count']);
      const bodyTotal = Number(data?.total || data?.pagination?.total);
      const totalCount = Number.isFinite(headerTotal) ? headerTotal : (Number.isFinite(bodyTotal) ? bodyTotal : null);
      if (Number.isFinite(totalCount)) setTotalPages(Math.max(1, Math.ceil(totalCount / 3)));
      else setTotalPages(null);
      // 서버가 plan_counts를 제공하면 즉시 매핑 (N+1 제거)
      const next = {};
      let hasServerCounts = false;
      for (const r of list) {
        if (r && r.plan_counts) { next[r.report_id] = r.plan_counts; hasServerCounts = true; }
      }
      if (hasServerCounts) setPlanStats(next);
    } catch (e) {
      // 백엔드 detail/message를 우선 사용하되, 상태코드별(403/404 등)로 한국어 기본 문구를 제공
      let msg = '보고서 목록 조회 중 오류가 발생했습니다.';
      if (typeof axios.isAxiosError === 'function' && axios.isAxiosError(e)) {
        const status = e?.response?.status;
        const backendDetail = e?.response?.data?.detail || e?.response?.data?.message;

        if (status === 403) {
          // 권한 없음: 백엔드 detail이 있으면 그대로 사용, 없으면 기본 권한 안내 문구
          msg = backendDetail || '리포트 목록을 조회할 권한이 없습니다.';
        } else if (status === 404) {
          // 리소스 없음: 백엔드 detail이 있으면 그대로 사용, 없으면 기본 안내 문구
          msg = backendDetail || '리포트 목록을 찾을 수 없습니다.';
        } else {
          // 기타 서버/네트워크 오류: 백엔드 메시지 → Axios 기본 메시지 → 기본 안내 문구 순으로 사용
          // [변경 사유]
          // - 기동 직후의 'Network Error'는 준비중 안내로 치환하여 불필요한 불안감을 줄입니다.
          msg = backendDetail || getFriendlyAxiosMessage(e, msg);
        }
      } else if (e && typeof e === 'object' && 'message' in e) {
        msg = getFriendlyAxiosMessage(e, msg);
      }

      setError(msg);
      setIsErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, getFriendlyAxiosMessage, includeSuperseded, page, query, userNo]);

  /**
   * [Why]
   * 서버가 페이지네이션 정보(전체 아이템 수)를 제공하지 않을 경우, 사용자 경험을 향상시키기 위해
   * 다음 페이지의 존재 여부를 미리 탐색(probe)합니다. 이를 통해 '다음' 버튼의 활성화/비활성화
   * 상태를 더 정확하게 제어할 수 있습니다.
   *
   * [How]
   * - 현재 페이지가 0이고 totalPages가 아직 확정되지 않았을 때만 실행됩니다.
   * - limit=1, offset=3(다음 페이지의 첫 아이템)으로 API를 호출하여, 응답이 있으면
   * 최소 2페이지 이상 존재한다고 판단하고 `totalPages`를 2로 설정합니다.
   */
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      if (!userNo) return;
      if (page !== 0) return; // 첫 페이지에서만 탐지 수행
      if (totalPages !== null) return; // 이미 확정된 경우 스킵
      try {
        const params = new URLSearchParams();
        if (query) params.set('q', query);
        if (!includeSuperseded) params.set('include_superseded', 'false');
        params.set('fields', 'report_id');
        params.set('with_plan_counts', 'false');
        params.set('limit', '1');
        params.set('offset', '3'); // 페이지당 3개이므로 다음 페이지 시작 지점
        const resp = await axios.get(`${apiUrl}/api/v1/users/${userNo}/reports?${params.toString()}`, { headers: authHeader() });
        if (cancelled) return;
        const data = resp?.data;
        const arr = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        setTotalPages(arr.length > 0 ? 2 : 1);
      } catch (_) {
        // 실패 시에는 기본값(2)로 표시 유지, 폴백 그대로 둠
      }
    };
    probe();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userNo, page, query, includeSuperseded]);

  /**
   * [Why]
   * 서버가 리포트 목록 조회 시 실천 계획 통계(`plan_counts`)를 함께 제공하지 않는 구 버전 API를
   * 위한 폴백(Fallback) 로직입니다. N+1 쿼리 문제를 발생시키지만, 하위 호환성을 보장합니다.
   *
   * [How]
   * - 조회된 리포트 목록(`items`)을 순회하며 각 리포트 ID에 대해 상세 조회 API를 추가로 호출합니다.
   * - 각 리포트의 상세 정보에서 실천 계획 목록을 가져와 상태별(완료, 진행중 등) 개수를 직접 계산하여
   * `planStats` 상태를 업데이트합니다.
   */
  useEffect(() => {
    let cancelled = false;
    const fetchDetails = async () => {
      if (!Array.isArray(items) || items.length === 0) return;
      // 서버 제공 plan_counts가 모든 아이템에 존재하면 폴백 생략
      const hasAllCounts = items.length > 0 && items.every(r => r && r.plan_counts);
      if (hasAllCounts) return;
      const next = {};
      for (const r of items) {
        try {
          const { data } = await axios.get(`${apiUrl}/api/v1/reports/${r.report_id}`, { headers: authHeader() });
          const goals = Array.isArray(data?.health_goals) ? data.health_goals : [];
          const plans = [];
          goals.forEach(g => (g.action_plans || []).forEach(p => plans.push(p)));
          const total = plans.length;
          const statusOf = (p) => (p?.completion_status || p?.status || '').toString();
          const completed = plans.filter(p => statusOf(p) === 'completed').length;
          const in_progress = plans.filter(p => statusOf(p) === 'in_progress').length;
          // 시작전: pending/not_started/empty 등을 포괄
          const not_started = plans.filter(p => ['pending','not_started','planned',''].includes(statusOf(p))).length;
          next[r.report_id] = { total, completed, in_progress, not_started };
        } catch (_) {
          next[r.report_id] = { total: undefined, completed: undefined, in_progress: undefined, not_started: undefined };
        }
      }
      if (!cancelled) setPlanStats(next);
    };
    fetchDetails();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const formatReportTitle = (r) => {
    // 백엔드에서 내려주는 title(환자명#report_id)을 우선 사용합니다.
    // title이 없을 때만 기존 날짜+리포트번호 포맷으로 폴백합니다.
    const t = (r?.title || '').toString().trim();
    const d = r?.created_at ? new Date(r.created_at) : null;
    // 날짜 표기는 "YYYY.MM.DD"로 통일합니다. (Intl 포맷의 공백/마침표 변형 이슈 회피)
    const dateText = (d && !isNaN(d.getTime()))
      ? `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
      : '';
    if (t) {
      // 화면 표시 개선: "환자명#8" 형태를 "환자명 #8"로 보이도록 공백을 정규화합니다.
      // (백엔드 값 자체/검색 로직에는 영향을 주지 않기 위해 프론트에서만 처리)
      const normalized = t.replace(/\s*#\s*/g, ' #');
      return dateText ? `${normalized} ${dateText}` : normalized;
    }
    if (dateText) return `리포트 #${r.report_id} ${dateText}`;
    return `리포트 #${r.report_id}`;
  };

  // userNo, query, page 등 주요 상태가 변경될 때마다 load 함수를 호출하여 데이터를 새로고침합니다.
  useEffect(() => {
    // 토큰과 로컬스토리지 동기화 보정
    const stored = localStorage.getItem('userNo');
    if (!userNo && stored) {
      // userNo가 훅 바깥에서 결정되는 환경 고려
      return;
    }
    load();
  }, [load, userNo]);

  // userNo가 변경될 때마다 로컬스토리지에 동기화합니다.
  useEffect(() => {
    window.localStorage.setItem('user_no', String(userNo || ''));
  }, [userNo]);

  const onSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const parseFilename = (disposition) => {
    try {
      if (!disposition) return null;
      // Content-Disposition: attachment; filename="report.pdf"; filename*=UTF-8''encoded
      const matchStar = /filename\*=[^']*''([^;]+)$/i.exec(disposition);
      if (matchStar && matchStar[1]) {
        return decodeURIComponent(matchStar[1]);
      }
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      if (match && match[1]) return match[1];
      return null;
    } catch {
      return null;
    }
  };

  /**
   * [Why]
   * 사용자가 'PDF 보기' 버튼을 클릭했을 때, 해당 리포트의 원본 PDF 파일을
   * 새 탭에서 열어주는 기능을 수행합니다.
   *
   * [How]
   * - `axios`를 사용하여 PDF 다운로드 API를 호출하고, 응답을 `blob` 형태로 받습니다.
   * - `URL.createObjectURL`을 사용하여 브라우저에서 접근 가능한 임시 URL을 생성합니다.
   * - `window.open`으로 새 탭을 열고, 팝업이 차단될 경우를 대비해 파일을 직접 다운로드하는
   * 폴백 로직을 포함합니다.
   *
   * @param {number} reportId - 열람할 리포트의 ID.
   */
  const openPdf = async (reportId) => {
    try {
      setDownloadingId(reportId);
      const url = `${apiUrl}/api/v1/reports/${reportId}/analysis-pdf`; // [변경 사유] 분석 리포트 PDF 다운로드
      const resp = await axios.get(url, { headers: authHeader(), responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(blob);
      const cd = resp?.headers?.['content-disposition'];
      const filename = parseFilename(cd) || `report-${reportId}.pdf`;

      // 다운로드 대신 새 탭에서 열기 (UX 상 미리보기 용도)
      const win = window.open(fileUrl, '_blank', 'noopener,noreferrer');
      if (!win) {
        // 팝업 차단 시, 강제로 다운로드 시도
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      // 일정 시간 후 메모리 회수
      setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000);
    } catch (e) {
      // PDF 열기 오류 시에도 백엔드 detail/message를 우선 사용하되, 상태코드별로 한국어 기본 문구를 제공
      let msg = 'PDF를 열 수 없습니다.';
      if (typeof axios.isAxiosError === 'function' && axios.isAxiosError(e)) {
        const status = e?.response?.status;
        const backendDetail = e?.response?.data?.detail || e?.response?.data?.message;

        if (status === 403) {
          // 권한 없음: 백엔드 detail이 있으면 그대로 사용, 없으면 기본 권한 안내 문구
          msg = backendDetail || '건강검진결과 PDF를 조회할 권한이 없습니다.';
        } else if (status === 404) {
          // 리포트 또는 PDF 없음: 백엔드 detail이 있으면 그대로 사용, 없으면 기본 안내 문구
          msg = backendDetail || '건강검진결과 PDF를 찾을 수 없습니다.';
        } else {
          // 기타 서버/네트워크 오류: 백엔드 메시지 → Axios 기본 메시지 → 기본 안내 문구 순으로 사용
          // [변경 사유]
          // - 기동 직후의 'Network Error'는 준비중 안내로 치환하여 불필요한 불안감을 줄입니다.
          msg = backendDetail || getFriendlyAxiosMessage(e, msg);
        }
      } else if (e && typeof e === 'object' && 'message' in e) {
        msg = getFriendlyAxiosMessage(e, msg);
      }

      setError(msg);
      setIsErrorModalOpen(true);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <h2 style={{ margin: 0 }}>분석 리포트 목록</h2>
        {/* 앱(WebView) 경로에서는 뒤로 버튼 숨김 */}
        {!isApp && (
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              height: 32, padding: '6px 10px', borderRadius: 999,
              border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)', cursor: 'pointer',
              fontSize: 14, lineHeight: 1
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            뒤로
          </button>
        )}
      </div>

      <form onSubmit={onSubmit} style={{
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginTop: 12
      }}>
        {/* 사용자번호는 히든 필드로만 유지 */}
        <input type="hidden" value={userNo} readOnly name="user_no" />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>검색</span>
          <input
            id="reports-search-query"
            name="q"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="제목/요약 검색"
            autoComplete="off"
            style={{ width: 180, height: 32, padding: '4px 8px', borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input
            id="reports-include-superseded"
            name="include_superseded"
            type="checkbox"
            checked={includeSuperseded}
            onChange={(e) => setIncludeSuperseded(e.target.checked)}
          />
          <span style={{ fontSize: 13, color: '#374151' }}>대체된 리포트 포함</span>
        </label>
        <button
          type="submit"
          style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
            height: 32, padding: '6px 10px', borderRadius: 999,
            border: '1px solid #6366f1', background: 'linear-gradient(180deg,#c7d2fe 0%,#a5b4fc 100%)',
            color: '#312e81', cursor: 'pointer', fontSize: 14,
            whiteSpace: 'nowrap', flexShrink: 0
          }}
        >
          검색
        </button>
      </form>

      {loading ? (
        // 스켈레톤 UI: content-visibility로 초기 페인트 비용 감소
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', padding: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', contentVisibility: 'auto', containIntrinsicSize: '120px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ height: 18, width: 220, background: '#f3f4f6', borderRadius: 6 }} />
                <div style={{ height: 18, width: 60, background: '#f3f4f6', borderRadius: 6 }} />
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <div style={{ height: 20, width: 70, background: '#f3f4f6', borderRadius: 999 }} />
                <div style={{ height: 20, width: 90, background: '#f3f4f6', borderRadius: 999 }} />
                <div style={{ height: 20, width: 80, background: '#f3f4f6', borderRadius: 999 }} />
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <div style={{ height: 28, width: 76, background: '#f3f4f6', borderRadius: 8 }} />
                <div style={{ height: 28, width: 82, background: '#f3f4f6', borderRadius: 8 }} />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: 16, color: '#6b7280' }}>표시할 리포트가 없습니다.</div>
      ) : (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          {items.map((r) => (
            <div key={r.report_id} style={{
              border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', padding: 12,
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)', contentVisibility: 'auto', containIntrinsicSize: '120px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {r.is_latest ? (
                    <span style={{ background: '#10b981', color: '#fff', padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>현재</span>
                  ) : (
                    <span style={{ background: '#9ca3af', color: '#fff', padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>과거</span>
                  )}
                  <strong style={{ fontSize: 15 }}>{formatReportTitle(r)}</strong>
                </div>
                {/* 생성일시는 사용자 요청에 따라 숨김 */}
                <div />
              </div>
              {/* 배지: 총 플랜 수 / 상태별 카운트 */}
              {(planStats[r.report_id]?.total !== undefined) && (
                <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ background: '#eef2ff', color: '#3730a3', padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>총 {planStats[r.report_id].total}건</span>
                  {typeof planStats[r.report_id].completed === 'number' && (
                    <span style={{ background: '#ecfeff', color: '#155e75', padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>완료 {planStats[r.report_id].completed}건</span>
                  )}
                  {typeof planStats[r.report_id].in_progress === 'number' && (
                    <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>진행중 {planStats[r.report_id].in_progress}건</span>
                  )}
                  {typeof planStats[r.report_id].not_started === 'number' && (
                    <span style={{ background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>시작전 {planStats[r.report_id].not_started}건</span>
                  )}
                </div>
              )}
              {r.summary && (
                <div style={{ marginTop: 6, color: '#4b5563' }}>{r.summary}</div>
              )}
              <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => openPdf(r.report_id)}
                  disabled={downloadingId === r.report_id}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    height: 30, padding: '6px 10px', borderRadius: 8,
                    border: '1px solid #d1d5db', background: '#fff', color: '#111827', cursor: downloadingId === r.report_id ? 'not-allowed' : 'pointer', fontSize: 13
                  }}
                  title="분석 리포트 PDF 보기"
                >
                  {downloadingId === r.report_id ? '열기...' : 'PDF 보기'}
                </button>
                <button
                  onClick={() => navigate(isApp ? `/app/reports/${r.report_id}` : `/reports/${r.report_id}`)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    height: 30, padding: '6px 10px', borderRadius: 8,
                    border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13
                  }}
                >
                  상세보기
                </button>
                <button
                  onClick={() => navigate(isApp ? `/app/action-plans/report/${r.report_id}` : `/action-plans/report/${r.report_id}`)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    height: 30, padding: '6px 10px', borderRadius: 8,
                    border: '1px solid #6366f1', background: 'linear-gradient(180deg,#c7d2fe 0%,#a5b4fc 100%)', color: '#1e1b4b', cursor: 'pointer', fontSize: 13
                  }}
                  title={r.is_latest ? '플랜 보기' : '대체된 리포트의 플랜으로 이동합니다'}
                >
                  플랜 보기
                </button>
              </div>
            </div>
          ))}
          {/* 페이지네이션 컨트롤 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            <button type="button" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: page === 0 || loading ? '#f3f4f6' : '#fff' }}>이전</button>
            <div style={{ color: '#6b7280', fontSize: 12 }}>
              {totalPages === null ? (
                <>페이지 {page + 1}</>
              ) : (
                <>페이지 {page + 1} / {totalPages}</>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPage(p => p + 1)}
              disabled={loading || ((totalPages !== null) ? (page + 1 >= totalPages) : (items.length < 3))}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: loading || ((totalPages !== null) ? (page + 1 >= totalPages) : (items.length < 3)) ? '#f3f4f6' : '#fff' }}
            >다음</button>
          </div>
        </div>
      )}

      {/* 리스트 및 PDF 관련 오류 안내용 메시지 모달 */}
      <MessageModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="알림"
        message={error}
        type="error"
      />
    </div>
  );
}

export default ReportsListPage;
